import { useState } from 'react';
import {
  Button,
  Tooltip,
  Progress,
  Tag,
  Card,
  Alert,
  Table,
  Space,
  Typography,
  Divider
} from 'antd';
import {
  ReloadOutlined,
  PlusOutlined,
  CreditCardOutlined,
  ArrowRightOutlined,
  RiseOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import endpoints from '../../api/endpoints';
import { useQuery } from '../../hooks/useApi';
import { paiseToRupees, formatDate, formatDateTime } from '../../utils/format';
import ErrorAlert from '../../components/ErrorAlert';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import StatusBadge from '../../components/StatusBadge';
import useAuth from '../../hooks/useAuth';
import AddSeatsModal from './AddSeatsModal';

const { Title, Text } = Typography;

// Stage 6A AntD-standards refactor + premium-SaaS redesign:
// 4 zones (Hero / Users / Pricing / Recent invoices) with every
// fact shown exactly once. Drops the redundant stat band + "Plan
// & period" detail card. Stronger value-first typography hierarchy
// (Stripe / Linear style).

function PriceRow({ label, value, tone, hint }) {
  return (
    <div className="flex items-baseline justify-between text-sm">
      <span className="text-ink-500">
        {label}
        {hint ? <span className="ml-1 text-ink-400">{hint}</span> : null}
      </span>
      <span
        className={`tabular-nums font-medium ${
          tone === 'success'
            ? 'text-emerald-600'
            : tone === 'muted'
              ? 'text-ink-500'
              : 'text-ink-900'
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function SubscriptionPage() {
  const { me, refreshMe } = useAuth();
  const [modalOpen, setModalOpen] = useState(false);

  const subQ = useQuery(() => endpoints.subscription.get(), []);
  const invQ = useQuery(
    () => endpoints.invoices.list({ page: 1, pageSize: 5 }),
    []
  );

  const sub = subQ.data;
  const userCounts = sub ? sub.users || sub.seats || null : null;
  const eff = sub ? sub.effectiveStatus : 'active';
  const isTrial = Boolean(
    sub && (sub.isTrial === true || sub.billingCycle === 'trial')
  );
  const canPurchase = !isTrial && (eff === 'active' || eff === 'reminder');
  const purchaseTooltip = isTrial
    ? 'Add-ons are not available on the Trial Plan. Upgrade to the Standard Plan to add more users.'
    : canPurchase
      ? null
      : 'Renew subscription before adding more users';

  // Progress-bar UX:
  //   under 90%  → blue   (normal)
  //   90 – 100%  → amber  (near / at limit)
  //   over 100%  → red    (exceeded — only possible with mid-cycle downgrades)
  const usedPct = userCounts
    ? Math.round((userCounts.used / Math.max(1, userCounts.total)) * 100)
    : 0;
  const usageTone =
    usedPct > 100 ? 'red' : usedPct >= 90 ? 'amber' : 'blue';
  const usageStroke =
    usageTone === 'red'
      ? '#DC2626'
      : usageTone === 'amber'
        ? '#F59E0B'
        : '#1877F2';
  const usageTagColor =
    usageTone === 'red' ? 'red' : usageTone === 'amber' ? 'gold' : 'blue';

  const recentInvoices = (invQ.data && invQ.data.items) || [];

  const invoiceColumns = [
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (v, row) => (
        <Link
          to={`/invoices/${row.id}`}
          className="text-brand-600 hover:text-brand-700 font-semibold font-mono text-[12.5px]"
        >
          {v}
        </Link>
      )
    },
    {
      title: 'Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      render: (v) => <span className="whitespace-nowrap">{formatDate(v)}</span>
    },
    {
      title: 'Total',
      dataIndex: 'totalAmountPaise',
      key: 'totalAmountPaise',
      align: 'right',
      render: (v) => (
        <span className="font-semibold tabular-nums whitespace-nowrap">
          {paiseToRupees(v)}
        </span>
      )
    }
  ];

  const subscriptionEndsAt =
    me && me.subscription ? me.subscription.subscriptionEndsAt : null;
  const graceEndsAt = me && me.subscription ? me.subscription.graceEndsAt : null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Subscription"
        subtitle="Manage your plan, users, and billing in one place."
        icon={<CreditCardOutlined />}
        actions={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                subQ.refetch();
                invQ.refetch();
                refreshMe();
              }}
            >
              Refresh
            </Button>
            <Link to="/subscription/renew">
              <Button
                type={
                  eff === 'reminder' || eff === 'grace' || eff === 'suspended'
                    ? 'primary'
                    : 'default'
                }
                icon={<CreditCardOutlined />}
              >
                {eff === 'reminder' || eff === 'grace' || eff === 'suspended'
                  ? 'Renew now'
                  : 'Renew'}
              </Button>
            </Link>
            {!isTrial && (
              <Tooltip title={purchaseTooltip}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  disabled={!canPurchase || subQ.loading}
                  onClick={() => setModalOpen(true)}
                >
                  Add users
                </Button>
              </Tooltip>
            )}
          </Space>
        }
      />

      <ErrorAlert error={subQ.error} />

      {subQ.loading || !sub ? (
        <LoadingBlock />
      ) : (
        <>
          {/* ============================================================
              HERO — single canonical place for plan / status / cycle /
              days remaining / renewal. Replaces the stat band + the
              "Plan & period" detail card so each fact is shown once.
          ============================================================ */}
          <Card className="!shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <Tag color="blue" className="!m-0 capitalize">
                    {sub.billingCycle}
                  </Tag>
                  <StatusBadge status={isTrial ? 'trialing' : eff} />
                  {isTrial ? (
                    <Tag color="green" className="!m-0">Trial active</Tag>
                  ) : null}
                </div>
                <Title
                  level={1}
                  className="!m-0 !text-3xl !font-bold !text-ink-900 capitalize"
                >
                  {sub.planCode}{' '}
                  <Text type="secondary" className="!text-base !font-normal">
                    v{sub.planVersion}
                  </Text>
                </Title>
                <Text type="secondary" className="block mt-1.5 text-sm">
                  <span className="font-semibold text-ink-700">
                    {sub.daysRemainingInPeriod}
                  </span>{' '}
                  of {sub.periodLengthDays} days remaining · period ends{' '}
                  {formatDate(sub.currentPeriodEnd)}
                </Text>
              </div>
              <div className="text-right shrink-0">
                <Text
                  type="secondary"
                  className="!text-[10px] uppercase tracking-[0.14em] font-semibold"
                >
                  Renewal due
                </Text>
                <p className="text-2xl font-semibold text-ink-900 m-0 mt-1 tracking-tight tabular-nums whitespace-nowrap">
                  {subscriptionEndsAt ? formatDate(subscriptionEndsAt) : '—'}
                </p>
                {graceEndsAt ? (
                  <p className="text-xs text-ink-400 m-0 mt-1">
                    Grace ends {formatDateTime(graceEndsAt)}
                  </p>
                ) : null}
              </div>
            </div>
          </Card>

          {/* Trial upgrade hint — only when on Trial Plan. */}
          {isTrial && (
            <Alert
              type="success"
              showIcon
              icon={<RiseOutlined />}
              message="Need more users?"
              description={`The Trial Plan includes ${
                userCounts ? userCounts.total : 5
              } users. Upgrade to the Standard Plan to add more.`}
              action={
                <Tooltip title="Contact support to upgrade your account.">
                  <Button
                    type="primary"
                    onClick={() =>
                      window.open(
                        'mailto:support@glass-i.com?subject=Upgrade%20from%20Trial%20Plan',
                        '_blank'
                      )
                    }
                  >
                    Contact support
                  </Button>
                </Tooltip>
              }
            />
          )}

          {/* ============================================================
              MAIN — Users + Pricing side by side.
              Value-first hierarchy: big number on top, label/caption
              below, in the Stripe / Linear style.
          ============================================================ */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* USERS */}
            <Card title="Users" className="!shadow-sm">
              {userCounts ? (
                <>
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <div className="text-[40px] leading-none font-bold text-ink-900 tracking-tight tabular-nums">
                        {userCounts.used}
                        <span className="text-ink-300 font-normal">
                          {' '}/ {userCounts.total}
                        </span>
                      </div>
                      <Text type="secondary" className="block mt-1 text-sm">
                        users active in this billing cycle
                      </Text>
                    </div>
                    <Tag color={usageTagColor} className="!m-0 !font-semibold">
                      {usedPct}% used
                    </Tag>
                  </div>
                  <Progress
                    percent={Math.min(usedPct, 100)}
                    showInfo={false}
                    strokeColor={usageStroke}
                    trailColor="#E2E8F0"
                    size={['100%', 8]}
                  />

                  <Divider className="!my-4" />

                  <div className="space-y-2">
                    <PriceRow
                      label="Add-on users purchased"
                      value={
                        userCounts.addonPurchased != null
                          ? userCounts.addonPurchased
                          : '—'
                      }
                    />
                    <PriceRow
                      label="Unassigned add-on"
                      value={
                        userCounts.addonAvailable != null
                          ? userCounts.addonAvailable
                          : '—'
                      }
                    />
                    <PriceRow
                      label="Available users"
                      value={userCounts.available}
                      tone={userCounts.available > 0 ? 'success' : 'muted'}
                    />
                  </div>
                </>
              ) : null}
            </Card>

            {/* PRICING */}
            <Card
              title="Pricing"
              className="!shadow-sm"
              styles={{ body: { paddingBottom: 0 } }}
            >
              <div className="space-y-2.5">
                <PriceRow
                  label="Per-user"
                  value={paiseToRupees(sub.pricing.pricePerUserPaise)}
                />
                <PriceRow
                  label="Base"
                  value={paiseToRupees(sub.pricing.baseAmountPaise)}
                />
                {sub.pricing.annualDiscountPercent > 0 && (
                  <PriceRow
                    label="Annual discount"
                    hint={`(${sub.pricing.annualDiscountPercent}%)`}
                    value={`− ${paiseToRupees(sub.pricing.discountPaise)}`}
                    tone="success"
                  />
                )}
                <PriceRow
                  label="GST"
                  hint={`(${sub.pricing.gstPercent}%)`}
                  value={paiseToRupees(sub.pricing.gstAmountPaise)}
                />
              </div>

              {/* Total payable — pulled into a brand-tinted footer band
                  flush with the card edges so the eye lands on the
                  final amount immediately. Mirrors the Stripe / Linear
                  invoice summary pattern. */}
              <div className="-mx-6 mt-5 px-6 py-4 bg-brand-50/70 border-t border-brand-100 rounded-b-[14px]">
                <div className="flex items-baseline justify-between">
                  <Text strong className="!text-ink-900">
                    Total payable
                  </Text>
                  <span className="text-2xl font-bold text-brand-700 tracking-tight tabular-nums">
                    {paiseToRupees(sub.pricing.totalAmountPaise)}
                  </span>
                </div>
                <Text type="secondary" className="block mt-1 text-xs text-right">
                  Charged at next renewal
                </Text>
              </div>
            </Card>
          </div>

          {/* RECENT INVOICES */}
          <Card
            title="Recent invoices"
            extra={
              <Link
                to="/invoices"
                className="text-[13px] font-semibold text-brand-600 hover:text-brand-700 inline-flex items-center gap-1 group"
              >
                See all
                <ArrowRightOutlined className="text-[10px] group-hover:translate-x-0.5 transition-transform" />
              </Link>
            }
            styles={{ body: { padding: 0 } }}
            className="!shadow-sm"
          >
            <ErrorAlert error={invQ.error} style={{ margin: 16 }} />
            <Table
              rowKey="id"
              dataSource={recentInvoices}
              columns={invoiceColumns}
              loading={invQ.loading}
              pagination={false}
              size="small"
              locale={{
                emptyText:
                  'No invoices yet — they appear here after each successful payment.'
              }}
            />
          </Card>
        </>
      )}

      <AddSeatsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        subscription={sub}
        onSuccess={() => {
          subQ.refetch();
          invQ.refetch();
          refreshMe();
        }}
      />
    </div>
  );
}
